/**
 * GO Commerce API Cache Utility
 *
 * Provides transparent caching for API calls with configurable TTLs.
 * Improves performance by reducing duplicate API calls and providing
 * instant data access for frequently used endpoints.
 */

// Declare global type for window.__lastApiCacheHit
declare global {
  interface Window {
    __lastApiCacheHit?: {
      endpoint: string;
      timestamp: number;
      accessCount?: number;
    };
    // Session management events and controls
    dispatchEvent(event: Event): boolean;
    __goSessionExpiryTimerId?: number;
    // Expose cache control methods
    disableGOCache?: () => void;
    enableGOCache?: () => void;
  }
}

interface CacheEntry {
  data: any;
  timestamp: number;
  accessCount?: number; // Track number of times this cache entry was accessed
  endpoint: string;
  ttl: number; // Store TTL with each entry
}

interface CacheConfig {
  ttl: number; // Time-to-live in milliseconds
  forceFresh?: boolean; // Whether to force a fresh fetch
  method?: string; // HTTP method (GET, POST, etc.)
  requestBody?: any; // Request body for POST requests
  useSharedCache?: boolean; // Whether to share cache across components
}

export class ApiCache {
  private static instance: ApiCache;
  private cache: Record<string, CacheEntry> = {};

  // Default TTL is 1 hour
  private defaultTTL = 60 * 60 * 1000;

  // Emergency kill switch
  private disabled = false;

  private constructor() {
    // Only do browser-specific operations when in a browser environment
    if (typeof window !== "undefined") {
      this.restoreFromStorage();
      // Set up periodic cache cleanup
      setInterval(() => this.cleanupExpiredEntries(), 5 * 60 * 1000); // Every 5 minutes

      // Expose disable method to window for emergency use
      (window as any).disableGOCache = this.disable.bind(this);
      (window as any).enableGOCache = this.enable.bind(this);
    }
  }

  static getInstance(): ApiCache {
    if (!ApiCache.instance) {
      ApiCache.instance = new ApiCache();
    }
    return ApiCache.instance;
  }

  disable(): void {
    this.disabled = true;
    console.log("[ApiCache] Cache has been disabled");
  }

  enable(): void {
    this.disabled = false;
    console.log("[ApiCache] Cache has been enabled");
  }

  /**
   * Main fetch method with caching
   * If cache is valid, returns cached data
   * Otherwise fetches fresh data and caches it
   */
  async fetch<T>(
    endpoint: string,
    fetchFn: () => Promise<T>,
    config?: CacheConfig
  ): Promise<T> {
    // Bypass cache if disabled
    if (this.disabled) {
      return fetchFn();
    }

    const cacheKey = this.getCacheKey(endpoint);
    const ttl = config?.ttl || this.defaultTTL;

    // Track in-flight requests to avoid duplicate calls when using shared cache
    // This is especially important for expensive GraphQL queries
    const useSharedCache = config?.useSharedCache ?? false;
    const requestKey = `request_${cacheKey}`;

    // Check for an in-progress shared request
    if (
      useSharedCache &&
      typeof window !== "undefined" &&
      (window as any)[requestKey]
    ) {
      console.log(`[ApiCache] 🔄 Joining in-flight request for: ${endpoint}`);
      try {
        // Wait for the in-flight request to complete
        return await (window as any)[requestKey];
      } catch (error) {
        // If the shared request fails, we'll try again ourselves
        console.warn(
          `[ApiCache] ⚠️ Shared request failed for ${endpoint}, retrying...`
        );
      }
    }

    // Check if we should use cache
    if (!config?.forceFresh && this.hasValidCache(cacheKey)) {
      console.log(`[ApiCache] 📦 Using cached data for: ${endpoint}`);

      // Increment access count for this cache entry
      if (this.cache[cacheKey]) {
        this.cache[cacheKey].accessCount =
          (this.cache[cacheKey].accessCount || 0) + 1;
        // Save to localStorage
        this.syncToStorage();
      }

      // Attach cache hit marker to window for monitoring to detect
      if (typeof window !== "undefined") {
        // Normalise endpoint for current-services so debug panel always detects it
        let normalisedEndpoint = endpoint;
        if (
          endpoint === "current-services" ||
          endpoint.endsWith("/current-services")
        ) {
          normalisedEndpoint = "current-services";
        }
        window.__lastApiCacheHit = {
          endpoint: normalisedEndpoint,
          timestamp: Date.now(),
          accessCount: this.cache[cacheKey]?.accessCount || 1,
        };
        console.log(
          `[ApiCache] ✅ Marked cache hit for debug: ${normalisedEndpoint}`
        );
      }

      return this.cache[cacheKey].data as T;
    }

    // Create a shared promise for this request if using shared cache
    let requestPromise: Promise<T>;

    // Store the promise globally if using shared cache
    if (useSharedCache && typeof window !== "undefined") {
      requestPromise = fetchFn();
      (window as any)[requestKey] = requestPromise;

      // Clean up the stored promise after completion or failure
      requestPromise.finally(() => {
        delete (window as any)[requestKey];
      });
    } else {
      requestPromise = fetchFn();
    }

    // Fetch fresh data
    try {
      console.log(`[ApiCache] 🌐 Fetching fresh data for: ${endpoint}`);
      const data = await requestPromise;
      this.setCache(cacheKey, data, endpoint, ttl);
      return data;
    } catch (error) {
      console.error(`[ApiCache] Error fetching data for ${endpoint}:`, error);

      // Check if this is an authentication error (401/403)
      if (this.isAuthenticationError(error)) {
        console.warn(
          "[ApiCache] Authentication failure detected, invalidating session cache"
        );
        // Invalidate auth-related caches
        this.invalidate("user-profile");

        // Trigger session expiration event for global handling
        if (typeof window !== "undefined") {
          const sessionExpiredEvent = new CustomEvent("go:session-expired", {
            detail: {
              reason: "auth_error",
              endpoint,
              timestamp: Date.now(),
            },
          });
          window.dispatchEvent(sessionExpiredEvent);

          // Don't return stale data for auth errors as it could cause more problems
          throw error;
        }
      }

      // For non-auth errors, we can still try to use stale data as fallback
      if (this.cache[cacheKey]) {
        console.log(`[ApiCache] Returning stale data for: ${endpoint}`);
        return this.cache[cacheKey].data as T;
      }

      throw error;
    }
  }

  // Force refresh of a specific endpoint
  async refreshEndpoint<T>(
    endpoint: string,
    fetchFn: () => Promise<T>,
    ttl?: number
  ): Promise<T> {
    if (this.disabled) {
      return fetchFn();
    }

    const cacheKey = this.getCacheKey(endpoint);

    try {
      console.log(`[ApiCache] Force refreshing data for: ${endpoint}`);
      const data = await fetchFn();
      this.setCache(cacheKey, data, endpoint, ttl || this.defaultTTL);
      return data;
    } catch (error) {
      console.error(`[ApiCache] Error refreshing data for ${endpoint}:`, error);

      // Check for auth errors during refresh
      if (this.isAuthenticationError(error)) {
        console.warn(
          "[ApiCache] Authentication failure detected during refresh, invalidating session cache"
        );
        this.invalidate("user-profile");

        if (typeof window !== "undefined") {
          const sessionExpiredEvent = new CustomEvent("go:session-expired", {
            detail: {
              reason: "auth_error_refresh",
              endpoint,
              timestamp: Date.now(),
            },
          });
          window.dispatchEvent(sessionExpiredEvent);
        }
      }

      throw error;
    }
  }

  invalidate(endpoint: string): void {
    const cacheKey = this.getCacheKey(endpoint);
    delete this.cache[cacheKey];
    this.syncToStorage();
    console.log(`[ApiCache] Invalidated cache for: ${endpoint}`);
  }

  invalidateAll(): void {
    this.cache = {};
    this.syncToStorage();
    console.log("[ApiCache] All cache entries invalidated");
  }

  private getCacheKey(endpoint: string): string {
    return `api_cache_${endpoint}`;
  }

  private hasValidCache(cacheKey: string): boolean {
    const entry = this.cache[cacheKey];
    if (!entry) return false;

    return Date.now() - entry.timestamp < entry.ttl;
  }

  private setCache(
    key: string,
    data: any,
    endpoint: string,
    ttl: number
  ): void {
    if (typeof window === "undefined") return;

    // Debug info to troubleshoot caching issues
    console.log(`[ApiCache] Saving to cache: ${key}`);

    this.cache[key] = {
      data,
      timestamp: Date.now(),
      accessCount: 1, // First access is the initial fetch
      endpoint,
      ttl,
    };

    // Explicitly update localStorage immediately
    this.syncToStorage();

    // Debug verification
    setTimeout(() => {
      try {
        const cacheData = JSON.parse(
          localStorage.getItem("go_api_cache") || "{}"
        );
        console.log(`[ApiCache] Verified cache save: ${key in cacheData}`);
      } catch (e) {
        console.error("[ApiCache] Error verifying cache save:", e);
      }
    }, 100);
  }

  private syncToStorage(): void {
    // Only sync to localStorage in browser environment
    if (typeof window === "undefined") return;

    try {
      localStorage.setItem("go_api_cache", JSON.stringify(this.cache));
    } catch (error) {
      console.error("[ApiCache] Error syncing to localStorage:", error);
    }
  }

  private restoreFromStorage(): void {
    // Skip restoration if not in browser environment
    if (typeof window === "undefined") return;

    try {
      const stored = localStorage.getItem("go_api_cache");
      if (stored) {
        this.cache = JSON.parse(stored);
        console.log("[ApiCache] Restored cache from localStorage");

        // Log restored endpoints for debugging
        const endpoints = Object.values(this.cache).map(
          (entry) => entry.endpoint
        );
        console.log("[ApiCache] Restored endpoints:", endpoints);
      }
    } catch (error) {
      console.error("[ApiCache] Error restoring from localStorage:", error);
      // If restore fails, reset cache
      this.cache = {};

      // Only try to remove if we're in a browser context
      if (typeof window !== "undefined") {
        try {
          localStorage.removeItem("go_api_cache");
        } catch (e) {
          // Silently fail if this also errors
        }
      }
    }
  }

  /**
   * Detect if an error is an authentication error (401/403)
   * This handles different error structures from various fetch/axios implementations
   */
  private isAuthenticationError(error: any): boolean {
    if (!error) return false;

    // Handle different error patterns
    // 1. Standard fetch response errors
    if (error.status === 401 || error.status === 403) return true;

    // 2. Axios-style errors with response property
    if (error.response?.status === 401 || error.response?.status === 403)
      return true;

    console.log(
      "[ApiCache] Checking for authentication error in:",
      error.data?.data
    );
    if (error.data?.data && error.data?.data?.statusCode === 401) return true;
    // 3. Error messages containing specific authentication failure indicators
    const errorMsg = String(error.message || "").toLowerCase();
    if (
      errorMsg.includes("unauthorized") ||
      errorMsg.includes("forbidden") ||
      errorMsg.includes("not logged in") ||
      errorMsg.includes("session expired") ||
      errorMsg.includes("invalid session") ||
      errorMsg.includes("no valid siteactiveid")
    )
      return true;

    // 4. Specific GO Integrate (n8n) errors
    if (
      error.detail?.includes?.("SITEACTIVEID") ||
      errorMsg.includes("siteactiveid")
    )
      return true;

    return false;
  }

  private cleanupExpiredEntries(): void {
    // Only run cleanup in browser environment
    if (typeof window === "undefined") return;

    const now = Date.now();
    let cleanedCount = 0;

    Object.keys(this.cache).forEach((key) => {
      const entry = this.cache[key];
      if (now - entry.timestamp > entry.ttl) {
        delete this.cache[key];
        cleanedCount++;
      }
    });

    if (cleanedCount > 0) {
      console.log(`[ApiCache] Cleaned up ${cleanedCount} expired entries`);
      this.syncToStorage();
    }
  }
}

// Create a simple singleton export
export const apiCache = ApiCache.getInstance();
